/**
 * Typed fetch wrapper for the shorty API.
 * credentials: same-origin — session cookie is HttpOnly.
 */

export interface ApiError {
	message: string;
	code?: string;
	details?: unknown;
}

export class ApiException extends Error {
	code?: string;
	status: number;
	details?: unknown;
	constructor(
		message: string,
		status: number,
		code?: string,
		details?: unknown,
	) {
		super(message);
		this.name = "ApiException";
		this.status = status;
		this.code = code;
		this.details = details;
	}
}

const API_BASE = "/api";

async function request<T>(
	path: string,
	options: RequestInit = {},
): Promise<T> {
	const headers = new Headers(options.headers);

	if (!headers.has("Content-Type") && options.body !== undefined) {
		if (!(options.body instanceof FormData)) {
			headers.set("Content-Type", "application/json");
		}
	}

	const res = await fetch(`${API_BASE}${path}`, {
		credentials: "same-origin",
		...options,
		headers,
	});

	if (res.status === 204) {
		return undefined as T;
	}

	const body = await res.json().catch(() => ({} as Record<string, unknown>));

	if (!res.ok) {
		const err = (body as { error?: ApiError }).error;
		throw new ApiException(
			err?.message ?? "Something went wrong",
			res.status,
			err?.code,
			err?.details,
		);
	}

	return body as T;
}

export const api = {
	get: <T>(path: string) => request<T>(path, { method: "GET" }),
	post: <T>(path: string, data?: unknown) =>
		request<T>(path, {
			method: "POST",
			body: data === undefined ? undefined : JSON.stringify(data),
		}),
	patch: <T>(path: string, data?: unknown) =>
		request<T>(path, {
			method: "PATCH",
			body: data === undefined ? undefined : JSON.stringify(data),
		}),
	del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
